import { useDispatch } from 'react-redux';
import DefaultPageLayout from '../../common/DefaultPageLayout';
import { useTypedSelector } from '../../store';
import {
  countriesSelector,
  getSelectedCountryCodes,
  selectBUsRaw,
  selectedDataLoadingSelector,
} from '../../store/selectData/selectors';
import { ProjectListSidebar } from '../../components/ProjectListSidebar/ProjectListSidebar';
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Stack } from '@mui/material';
import ApprovalPageStats from '../../components/ApprovalPageStats';
import Text from '@ingka/text';
import ArchiveProjectPrompt from '../../components/ArchiveProjectPrompt';
import ChangeProjectStatusPrompt from '../../components/ChangeProjectStatusPrompt';
import DeleteProjectConfirmationPrompt from '../../components/DeleteProjectConfirmationPrompt';
import {
  getLoading,
  selectAccessibleProjects,
  selectProjectToEditWithFormattedInvestments,
} from '../../store/projects/selectors';
import {
  archiveProjects,
  clearSolutions,
  deleteProjects,
  fetchProjects,
  getProjectDetails,
  setProjectsForBulk,
  updatePartOfCrp,
  updatePartOfApprovedCrp,
  updateProjectsStatus,
  updateSingleProjectStatus,
} from '../../store/projects/actionWrapper';
import { ProjectListTable } from '../../components/ProjectListTable/ProjectListTable';
import CreateNewUnitModal from '../CreateNewUnitModal';
import ImproveProjectModal from '../ImproveProjectModal';
import FormatChangeModal from '../FormatChangeModal';
import RelocationModal from '../RelocationModal';
import CloseProjectModal from '../CloseProjectModal';
import ProjectTypeSelect from '../../components/ProjectTypeSelect';
import CustomModal from '../../components/CustomModal';
import useTranslation from '../../hooks/useTranslation';
import { modalsActions } from '../../store/modals/slice';
import { selectProjectStatusModal } from '../../store/modals/selectors';
import { projectsActions } from '../../store/projects/slice';
import { getProjectsToFetch, returnAllowedStatuses } from '../../utils/utils';
import LoadingScreen from '../../common/LoadingScreen';
import { selectCountryCodes } from '../../store/selectData/actionWrappers';
import { selectUserRole } from '../../store/userData/selectors';
import { useHistory, useParams } from 'react-router-dom';
import EditProjectModals from '../../components/EditProjectModals';
import { useAnalytics } from '../../hooks/useAnalytics';
import PartOfCrpModal from '../../components/PartOfCrpModal';
import PartOfApprovedCrpModal from '../../components/PartOfApprovedCrpModal';

export const ProjectListPage = () => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { actionStart, actionEnd, actionDiscarded } =
    useAnalytics('projectCreate');
  const {
    actionStart: actionStartEdit,
    actionEnd: actionEndEdit,
    actionDiscarded: actionDiscardEdit,
  } = useAnalytics('projectUpdated');
  const history = useHistory();
  const countryList = useTypedSelector(countriesSelector);
  const countryOptions = countryList.map((c) => c.countryName);
  const [stats, setStats] = useState([]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { projectId } = useParams();
  const businessUnits = useTypedSelector(selectBUsRaw);
  const loading = useTypedSelector(selectedDataLoadingSelector);
  const selectedCountryCodes = useTypedSelector(getSelectedCountryCodes);
  const [isProjectModal, setIsProjectModal] = useState(false);
  const [isArchiveProjectModal, setIsArchiveProjectModal] = useState(false);

  const [isDeleteProjectModal, setIsDeleteProjectModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const projectsList = useTypedSelector(selectAccessibleProjects);
  const isLoading = useTypedSelector(getLoading);
  const [tableSelection, setTableSelection] = useState<string[]>([]);
  const [isNewUnitModal, setIsNewUnitModal] = useState(false);

  const [isCreateImproveModal, setIsCreateImproveModal] = useState(false);
  const [isFormatChangeModal, setIsFormatChangeModal] = useState(false);
  const [isRelocationModal, setIsRelocationModal] = useState(false);
  const [isCloseProjectModal, setIsCloseProjectModal] = useState(false);
  const [editProjectEstablishmentType, setEditProjectEstablishmentType] =
    useState(null);
  const [openWithDirective, setOpenWithDirective] = useState(false);
  const [allowedStatuses, setAllowedStatuses] = useState([]);
  const [partOfCrpModalVisible, setPartOfCrpModalVisible] = useState(false);
  const [partOfApprovedCrpModalVisible, setPartOfApprovedCrpModalVisible] =
    useState(false);
  const [partOfApprovedCrpButtonDisabled, setPartOfApprovedCrpButtonDisabled] =
    useState(false);
  const isChangeProjectStatusModal = useTypedSelector(selectProjectStatusModal);
  const formattedProject = useTypedSelector(
    selectProjectToEditWithFormattedInvestments,
  );

  const role = useTypedSelector(selectUserRole);
  const handleOpenCreateProjectModal = () => {
    setIsProjectModal(true);
  };
  const handleCloseProjectModal = () => {
    setIsProjectModal(false);
    actionDiscarded();
    dispatch(clearSolutions());
  };

  const handleOpenArchiveModal = () => {
    setIsArchiveProjectModal(true);
  };
  const handleCloseArchiveModal = () => {
    setIsArchiveProjectModal(false);
    dispatch(clearSolutions());
  };

  const handleCancelArchiveProjects = () => {
    setIsArchiveProjectModal(false);
  };
  const handleAcceptArchiveProjects = async () => {
    await dispatch(archiveProjects(tableSelection));
    const countryNames = selectedCountryCodes.map(
      (el) =>
        countryList.find((c) => c.countryId === el.countryId)?.countryName,
    );

    dispatch(fetchProjects(getProjectsToFetch(countryNames, countryList)));

    setTableSelection([]);
    setIsArchiveProjectModal(false);
  };

  const handleDeleteProject = async () => {
    if (!tableSelection.length) return;

    const projectId = tableSelection[0];
    await dispatch(deleteProjects(projectId));

    setTableSelection([]);
    setIsDeleteProjectModal(false);
  };

  const handleOpenDeleteProjectModal = () => {
    setIsDeleteProjectModal(true);
  };
  const handleCloseDeleteProjectModal = () => {
    setIsDeleteProjectModal(false);
  };

  const handleOpenProjectStatusModal = () => {
    dispatch(modalsActions.openProjectStatusModal());
  };
  const handleCloseChangeProjectStatusModal = () => {
    dispatch(modalsActions.closeProjectStatusModal());
    setSelectedStatus('');
  };

  const openPartOfCrpModal = () => {
    setPartOfCrpModalVisible(true);
  };
  const closePartOfCrpModal = () => {
    setPartOfCrpModalVisible(false);
  };
  const handlePartOfCrpChange = (fyArr: string[]) => {
    dispatch(
      updatePartOfCrp({
        projectIds: tableSelection,
        fiscalYears: fyArr,
      }),
    );
    closePartOfCrpModal();
  };

  const openPartOfApprovedCrpModal = () => {
    setPartOfApprovedCrpModalVisible(true);
  };
  const closePartOfApprovedCrpModal = () => {
    setPartOfApprovedCrpModalVisible(false);
  };
  const updatePartOfApprovedCrpButtonVisibility = (
    hasCommonCrpFys: boolean,
  ) => {
    setPartOfApprovedCrpButtonDisabled(!hasCommonCrpFys);
  };
  const handlePartOfApprovedCrpChange = (fyArr: string[]) => {
    dispatch(
      updatePartOfApprovedCrp({
        projectIds: tableSelection,
        fiscalYears: fyArr,
      }),
    );
    closePartOfApprovedCrpModal();
  };

  const handleChangeProjectStatus = () => {
    dispatch(modalsActions.closeProjectStatusModal());
    if (tableSelection.length === 1) {
      dispatch(
        updateSingleProjectStatus({
          projectId: tableSelection[0],
          status: selectedStatus,
        }),
      );
    } else {
      dispatch(
        updateProjectsStatus({
          projects: tableSelection,
          status: selectedStatus,
        }),
      );
    }
    setTableSelection([]);
  };

  const handleChangeStatus = (status: string) => {
    setSelectedStatus(status);
  };

  const handleOpenEditProjectModal = (projectId: string) => {
    dispatch(getProjectDetails({ id: projectId, refId: true }));
    actionStartEdit();
    history.push(`/project/${projectId}`);
  };

  const handleOpenCreateNewUnitModal = () => {
    setIsNewUnitModal(true);
  };
  const handleCloseCreateNewUnitModal = () => {
    setIsNewUnitModal(false);
    dispatch(clearSolutions());
    actionDiscarded();
  };

  const handleOpenCreateImproveModal = () => {
    setIsCreateImproveModal(true);
  };
  const handleCloseImproveModal = () => {
    setIsCreateImproveModal(false);
    actionDiscarded();
    dispatch(clearSolutions());
  };
  const handleOpenFormatChangeModal = () => {
    setIsFormatChangeModal(true);
  };

  const handleCloseFormatChangeModal = () => {
    setIsFormatChangeModal(false);
    actionDiscarded();
    dispatch(clearSolutions());
  };

  const handleOpenRelocationModal = () => {
    setIsRelocationModal(true);
  };

  const handleCloseRelocationModal = () => {
    setIsRelocationModal(false);
    actionDiscarded();
    dispatch(clearSolutions());
  };

  const handleOpenCloseProjectModal = () => {
    setIsCloseProjectModal(true);
  };

  const handleCloseCloseProjectModal = () => {
    setIsCloseProjectModal(false);
    actionDiscarded();
    dispatch(clearSolutions());
  };

  const closeEditModal = () => {
    setEditProjectEstablishmentType(null);
    setOpenWithDirective(false);
    actionDiscardEdit();
    history.push('/', {
      from: 'project',
    });
    setTimeout(() => {
      dispatch(projectsActions.clearProject());
      dispatch(clearSolutions());
    }, 100);
  };

  const setActiveProject = (projectId: string) => {
    setTableSelection([projectId]);
  };
  const onProjectCreated = (type) => {
    actionEnd({
      eventSubType: 'Modal',
      context: { ProjectType: type },
    });
  };

  const onCountryCodeChange = (countryCodes: string[]) => {
    if (countryCodes.includes('All')) {
      dispatch(selectCountryCodes(countryList));
    } else {
      dispatch(
        selectCountryCodes(
          countryCodes.map((el) =>
            countryList.find((c) => c.countryName === el),
          ),
        ),
      );
    }
    if (countryCodes.length != 0) {
      dispatch(fetchProjects(getProjectsToFetch(countryCodes, countryList)));
    }
  };
  const bulkEditHandler = () => {
    dispatch(setProjectsForBulk(tableSelection));
    setTableSelection([]);
    history.push('/bulk-edit');
  };
  const bulkCreateHandler = () => {
    history.push('/bulk-create');
  };

  const handleDirectiveClicked = (id: string) => {
    setOpenWithDirective(true);
    dispatch(getProjectDetails({ id, refId: true }));
    actionStartEdit();
  };

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectDetails({ id: projectId, refId: true }));
      actionStartEdit();
    }
  }, []);

  useEffect(() => {
    if (formattedProject) {
      setEditProjectEstablishmentType(formattedProject.establishmentType);
    }
  }, [formattedProject]);

  useEffect(() => {
    if (selectedCountryCodes.length > 0 && !loading) {
      dispatch(
        fetchProjects(
          getProjectsToFetch(
            selectedCountryCodes.map((el) => el.countryName),
            countryList,
          ),
        ),
      );
    }
  }, [businessUnits]);

  useEffect(() => {
    if (isChangeProjectStatusModal) {
      const statusesList = returnAllowedStatuses(
        projectsList.filter((project) => tableSelection.includes(project.id)),
        role,
      );

      setSelectedStatus(statusesList[0]);
      setAllowedStatuses(statusesList);
    }
  }, [tableSelection, isChangeProjectStatusModal]);

  return (
    <>
      {loading ? (
        <LoadingScreen message={t('flight.loadingProjects')} />
      ) : (
        <>
          <DefaultPageLayout
            sidebar={() => (
              <ProjectListSidebar
                countryList={countryList}
                selectedCountryCodes={selectedCountryCodes}
                handleOpenCreateProjectModal={handleOpenCreateProjectModal}
                onCountryCodeChange={onCountryCodeChange}
                bulkCreateHandler={bulkCreateHandler}
              />
            )}
            mainPart={() => (
              <Stack px={2} height="100%" spacing={4} mt={4}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  flexShrink={0}
                >
                  <Text style={{ flex: 4 }} textStyle={'Heading.L'}>
                    Projects
                  </Text>
                  <ApprovalPageStats stats={stats} loading={isLoading} />
                </Stack>
                <Box flexGrow={1} minHeight={0}>
                  <ProjectListTable
                    projects={projectsList}
                    isLoading={isLoading}
                    tableSelection={tableSelection}
                    setTableSelection={setTableSelection}
                    handleDeleteProject={handleOpenDeleteProjectModal}
                    handleArchiveProject={handleOpenArchiveModal}
                    handleChangeProjectStatus={handleOpenProjectStatusModal}
                    handleOpenEditProjectModal={handleOpenEditProjectModal}
                    bulkEditHandler={bulkEditHandler}
                    selectedCountries={selectedCountryCodes}
                    countries={countryOptions}
                    openPartOfCrpHandler={openPartOfCrpModal}
                    openPartOfApprovedCrpHandler={openPartOfApprovedCrpModal}
                    partOfApprovedCrpButtonDisabled={
                      partOfApprovedCrpButtonDisabled
                    }
                    setStats={setStats}
                    directiveClickedEvent={handleDirectiveClicked}
                  />
                </Box>
              </Stack>
            )}
          />

          <CustomModal
            testid={'create-new-project-type-modal'}
            title={t('actions.createNewUnit')}
            onClose={handleCloseProjectModal}
            children={
              <ProjectTypeSelect
                closeCreateModal={handleCloseProjectModal}
                handleOpenCreateNewUnitModal={handleOpenCreateNewUnitModal}
                handleOpenCreateImproveModal={handleOpenCreateImproveModal}
                handleOpenFormatChangeModal={handleOpenFormatChangeModal}
                handleOpenRelocationModal={handleOpenRelocationModal}
                handleOpenCloseProjectModal={handleOpenCloseProjectModal}
                startAction={actionStart}
              />
            }
            isVisible={isProjectModal}
          />
          {/*CREATE MODALS*/}
          <CreateNewUnitModal
            visible={isNewUnitModal}
            onClose={() => {
              handleCloseCreateNewUnitModal();
              handleOpenCreateProjectModal();
            }}
            projectsList={projectsList}
            countries={countryList}
            setActiveProject={setActiveProject}
            projectCreatedTrigger={() => onProjectCreated('NEW')}
          />
          <ImproveProjectModal
            visible={isCreateImproveModal}
            onClose={() => {
              handleCloseImproveModal();
              handleOpenCreateProjectModal();
            }}
            projectsList={projectsList}
            countries={countryList}
            setActiveProject={setActiveProject}
            projectCreatedTrigger={() => onProjectCreated('SISAN')}
          />
          <FormatChangeModal
            visible={isFormatChangeModal}
            onClose={() => {
              handleCloseFormatChangeModal();
              handleOpenCreateProjectModal();
            }}
            projectsList={projectsList}
            countries={countryList}
            setActiveProject={setActiveProject}
            projectCreatedTrigger={() => onProjectCreated('FORMAT_CHANGE')}
          />
          <RelocationModal
            visible={isRelocationModal}
            onClose={() => {
              handleCloseRelocationModal();
              handleOpenCreateProjectModal();
            }}
            projectsList={projectsList}
            countries={countryList}
            setActiveProject={setActiveProject}
            projectCreatedTrigger={() => onProjectCreated('RELOCATE')}
          />
          <CloseProjectModal
            visible={isCloseProjectModal}
            onClose={() => {
              handleCloseCloseProjectModal();
              handleOpenCreateProjectModal();
            }}
            projectsList={projectsList}
            countries={countryList}
            setActiveProject={setActiveProject}
            projectCreatedTrigger={() => onProjectCreated('CLOSE')}
          />

          {/*EDIT MODALS*/}

          <EditProjectModals
            closeHandler={closeEditModal}
            establishmentType={editProjectEstablishmentType}
            project={formattedProject}
            projectsList={projectsList}
            countries={countryList}
            submitTrigger={() =>
              actionEndEdit({
                eventSubType: 'Modal',
                context: { ProjectType: editProjectEstablishmentType },
              })
            }
            setActiveProject={setActiveProject}
            directiveOpen={openWithDirective}
          />

          <ArchiveProjectPrompt
            isVisible={isArchiveProjectModal}
            onClose={handleCloseArchiveModal}
            onCancel={handleCancelArchiveProjects}
            onAccept={handleAcceptArchiveProjects}
          />
          <ChangeProjectStatusPrompt
            isVisible={isChangeProjectStatusModal}
            onClose={handleCloseChangeProjectStatusModal}
            onCancel={handleCloseChangeProjectStatusModal}
            onAccept={handleChangeProjectStatus}
            selectedStatus={selectedStatus}
            onStatusChange={handleChangeStatus}
            allowedStatuses={allowedStatuses}
          />
          <DeleteProjectConfirmationPrompt
            isVisible={isDeleteProjectModal}
            onClose={handleCloseDeleteProjectModal}
            onCancel={handleCloseDeleteProjectModal}
            onAccept={handleDeleteProject}
          />
          <PartOfCrpModal
            visible={partOfCrpModalVisible}
            closeHandler={closePartOfCrpModal}
            approveHandler={handlePartOfCrpChange}
            projects={projectsList}
            selectedProjects={tableSelection}
          />
          <PartOfApprovedCrpModal
            visible={partOfApprovedCrpModalVisible}
            closeHandler={closePartOfApprovedCrpModal}
            approveHandler={handlePartOfApprovedCrpChange}
            projects={projectsList}
            selectedProjects={tableSelection}
            onCommonCrpFyChangeHandler={updatePartOfApprovedCrpButtonVisibility}
          />
        </>
      )}
    </>
  );
};
